/* eslint-disable */
import { Badge, Button } from "@mui/material";
import {
  DateCalendar,
  DayCalendarSkeleton,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { timeZoneList } from "../../utils";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import * as yup from "yup";
import RHFSelect from "src/components/elements/Select/RHFSelect";
import AddNewSlots from "../AddNewSlots";
import {
  AddSlotsProps,
  CreateInterviewSlotsRequest,
  HighlightedDay,
  HighlightedDaysRequest,
  MonthSlotProps,
  SlotsProps,
} from "../../types";
import { RiDeleteBinLine } from "react-icons/ri";
import { useRoleUser } from "src/hooks/query/usersByRole";
import { useAuth } from "@panna/auth";
import RHFAutoComplete from "src/components/elements/AutoComplete/RHFAutoComplete";
import { confirmWrapper } from "src/components/Dialog";

interface CreateSlotsProps {
  highlightedDays: Array<HighlightedDay>;
  isCalendarLoading: boolean;
  monthSlots: MonthSlotProps;
  isATSRecruiter: boolean;
  isATSInterviewer: boolean;
  getSlotsByMonth: (data: HighlightedDaysRequest) => Promise<void>;
  saveInterviewSlots: (data: CreateInterviewSlotsRequest) => Promise<void>;
  deleteInterviewSlot: (slotId: number) => Promise<void>;
}

interface CreateNewSlotsProps {
  timeZone: string;
  interviewers?: number;
}

const CreateSlots = function CreateSlots(props: CreateSlotsProps) {
  const {
    highlightedDays,
    isCalendarLoading,
    monthSlots,
    isATSRecruiter,
    isATSInterviewer,
    getSlotsByMonth,
    saveInterviewSlots,
    deleteInterviewSlot,
  } = props;

  const { userByRoleOptionsWithEmail, usersLoading, fetchByRole } = useRoleUser({
    roleNameList: "ATSInterviewer",
  });

  const [slotsList, setSlotsList] = useState<AddSlotsProps[]>([]);
  const [date, setDate] = useState<number | undefined>(new Date().valueOf());
  const [daySlots, setDaySlots] = useState<SlotsProps[]>();

  const authContext = useAuth();
  const { authState: auth } = authContext;
  const userId = auth?.userInfo?.id;

  const validationSchema = yup.object().shape({
    timeZone: yup
      .string()
      .typeError("Select time zone")
      .required("Time zone required"),
      interviewers: yup.number()
      .typeError("Select interviewer")
      .required("Interviewer required"),
      // interviewers: yup
      // .number()
      // .nullable()
      // .test('conditional-validation', 'Please select an interviewer',(value, context)=>{
      //   if (isATSRecruiter && !isATSInterviewer) {
      //     return value !== null && value !== undefined;
      //   }
      //   return true;
      // }),
  });

  const resolver = useYupValidationResolver(validationSchema);

  const methods = useForm<CreateNewSlotsProps>({
    defaultValues: {
      timeZone: "IST",
      interviewers: isATSInterviewer ? userId as number : undefined,
    },
    resolver,
  });

  const { handleSubmit, watch } = methods;

  const submitHandler: SubmitHandler<CreateNewSlotsProps> = async (data) => {
    try {
      handleCreateNewSlots();
    } catch (e) {
      console.info("...");
    }
  };

  const handleAddNewSlotToList = (slots: AddSlotsProps[]) => {
    setSlotsList(slots);
  };

  const removeSlotFromSlotList = (id: string | undefined) => {
    setSlotsList((prevSlots) => prevSlots.filter((item) => item.id !== id));
  };

  const handleMonthOrYearChange = (date: Dayjs) => {
    setDate(date.valueOf());
    setDaySlots(undefined);
    const interviewerId = watch("interviewers");
    const data: HighlightedDaysRequest = {
      month: date.month(),
      year: date.year(),
      interviewerId: interviewerId ? interviewerId : userId,
    };
    getSlotsByMonth(data);
  };

  const handleCreateNewSlots = async () => {
    const timeZone = watch("timeZone");
    const interviewerId = watch("interviewers");

    // Helper function to convert time to minutes since start of day
    const timeToMinutes = (time: string | null): number => {
      if (!time) return 0;
      const [hour, minute] = moment(time, "hh:mm A")
        .format("HH:mm")
        .split(":")
        .map(Number);
      return hour * 60 + minute;
    };

    // Function to check for overlapping slots
    const hasOverlap = (slot1: AddSlotsProps, slot2: SlotsProps): boolean => {
      // Allow overlapping if time zones are different
      if (timeZone !== slot2.timeZone) return false;

      const start1 = timeToMinutes(slot1.startTime);
      const end1 = timeToMinutes(slot1.endTime);
      const start2 = timeToMinutes(slot2.startTime);
      const end2 = timeToMinutes(slot2.endTime);
      return start1 < end2 && start2 < end1;
    };

    // Function to check for duplicates
    const isDuplicate = (slot1: AddSlotsProps, slot2: SlotsProps): boolean => {
      return (
        slot1.startTime === slot2.startTime &&
        slot1.endTime === slot2.endTime &&
        timeZone === slot2.timeZone
      );
    };

    // Check for duplicates and overlaps
    const existingSlots =
      daySlots || monthSlots?.[moment(date).format("MM/DD/YYYY")] || [];
    for (const newSlot of slotsList) {
      for (const existingSlot of existingSlots) {
        if (isDuplicate(newSlot, existingSlot)) {
          confirmWrapper(
            `Duplicate slot detected: ${newSlot.startTime} - ${newSlot.endTime} ${timeZone}`,
            {
              hideSecondaryBtn: true,
              variant: "danger",
            }
          );
          return;
        }
        if (hasOverlap(newSlot, existingSlot)) {
          confirmWrapper(
            `Overlapping slot detected: ${newSlot.startTime} - ${newSlot.endTime} ${timeZone}`,
            {
              hideSecondaryBtn: true,
              variant: "danger",
            }
          );
          return;
        }
      }
    }

    const finalData: CreateInterviewSlotsRequest = {
      timeSlots: slotsList,
      date: moment(date).format("MM/DD/YYYY"),
      timeZone,
      interviewerId: interviewerId ? interviewerId : (userId as number),
    };
    await saveInterviewSlots(finalData);
    setSlotsList([]);
    handleMonthOrYearChange(dayjs(date));
  };

  const handleDeleteSlot = async (slotId: number) => {
    await deleteInterviewSlot(slotId);
    handleMonthOrYearChange(dayjs(date));
  };

  const handleDateChange = (date: number | undefined) => {
    if (date !== undefined) {
      const formattedDate: string = moment(date).format("MM/DD/YYYY");
      const daySlots = monthSlots?.[formattedDate];
      setDaySlots(daySlots);
    }
  };

  function ServerDay(props: PickersDayProps<Dayjs>) {
    const { day, outsideCurrentMonth, ...other } = props;

    const dayString = day.format("MM/DD/YYYY"); // Format the day to match the format of highlightedDays

    const highlightedDay = highlightedDays.find(
      (highlight) => highlight.date === dayString
    );

    const isSelected = !outsideCurrentMonth && highlightedDay !== undefined;

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={
          isSelected ? (
            <div className="w-[10px] h-[10px] flex justify-center rounded-xl bg-lightGray border-[1px] border-solid border-primary absolute top-1 left-0 text-primary font-extrabold text-[8px]">
              {highlightedDay?.slots}
            </div>
          ) : undefined
        }
      >
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
        />
      </Badge>
    );
  }

  useEffect(() => {
    handleMonthOrYearChange(dayjs(date));
  }, [watch("interviewers")]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <div className="flex flex-col gap-5 p-10 text-left">
            <div>
              <strong>Create Time Slot: </strong>(Select date to create multiple
              slots in one shot)
            </div>
            <div className="flex gap-5">
              <div>
                {isATSRecruiter && (
                  <div className="mb-7">
                    <div className="font-bold text-md text-[#28364C] tracking-wide mb-1">
                      Select interviewer
                    </div>
                    <RHFAutoComplete
                      name="interviewers"
                      control={methods.control}
                      options={userByRoleOptionsWithEmail}
                      loading={usersLoading}
                      placeholder="Select interviewer"
                    />
                  </div>
                )}

                <DateCalendar
                  defaultValue={dayjs(new Date())}
                  minDate={dayjs(new Date())}
                  loading={isCalendarLoading}
                  onMonthChange={handleMonthOrYearChange}
                  onYearChange={handleMonthOrYearChange}
                  renderLoading={() => <DayCalendarSkeleton />}
                  onChange={(e) => {
                    const dateValue = e?.valueOf();
                    setDate(dateValue);
                    handleDateChange(dateValue);
                  }}
                  slots={{
                    day: ServerDay,
                  }}
                  slotProps={{
                    day: {
                      highlightedDays,
                    } as any,
                  }}
                  className="shadow-md bg-white"
                />
                <div>
                  {slotsList.length > 0 && (
                    <div className="text-sm text-primary mb-2">
                      You have entered {slotsList.length} new slot
                      {slotsList.length > 1 ? "s" : ""}
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="w-[150px] mt-5"
                    disabled={slotsList.length == 0}
                  >
                    Create Slot
                  </Button>
                </div>
              </div>
              <div className="w-[300px]">
                <RHFSelect
                  control={methods.control}
                  name="timeZone"
                  label="Time zone"
                  options={timeZoneList}
                />
                <AddNewSlots
                  handleAddNewSlotToList={handleAddNewSlotToList}
                  removeSlotFromSlotList={removeSlotFromSlotList}
                  slotsList={slotsList}
                />
                <div className="mt-1">
                  {daySlots && (
                    <div className="text-[#4D586A]">
                      <div className="font-semibold">Open Slots:</div>
                      <div className="max-h-[400px] w-fit overflow-y-auto mt-2 flex flex-col gap-2">
                        {daySlots?.map((slot: SlotsProps, index: number) => {
                          return (
                            <div
                              key={index}
                              className="w-fit group hover:bg-[#4d586a0f] p-1 rounded-md"
                            >
                              <div className="flex items-center gap-2 cursor-default w-fit">
                                <div className="flex gap-2">
                                  <div>{slot.startTime}</div>
                                  <div>-</div>
                                  <div>{slot.endTime}</div>
                                  <div>{slot.timeZone}</div>
                                </div>
                                <div
                                  onClick={() => {
                                    handleDeleteSlot(slot.id);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xl text-secondary hover:text-primary cursor-pointer"
                                >
                                  <RiDeleteBinLine />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </LocalizationProvider>
      </form>
    </FormProvider>
  );
};

CreateSlots.defaultProps = {};

CreateSlots.propTypes = {};

export default CreateSlots;
